/*
  Adapts the push API's error shape to the one the share page's error panel
  already speaks.

  A failed push answers with

    { error: { message, errors: [ { index, "<field>": [messages] } ] } }

  so each entry carries the row index alongside the field map, flat. The panel
  feeds mergeErrorsByField, which expects that map nested under `errors`.

  On main these two helpers live in ./upload-error-list.jsx, extracted by
  CTD-12908. That extraction is not on prod, so they are inlined here; the next
  main -> prod merge should drop them and import from upload-error-list.jsx.
*/

// The API uses an empty-string key for a message that belongs to the payload as
// a whole rather than to a field. The panel bolds whatever precedes the first
// colon, so an empty label would render as a stray colon.
const PAYLOAD_LABEL = 'Payload';

/**
 * Merges the per-row error maps a validation run returns into one field -> messages
 * map, so a message repeated across rows is shown once.
 */
export function mergeErrorsByField(validationErrors = []) {
	const merged = {};

	for (const { errors } of validationErrors) {
		for (const [path, messages] of Object.entries(errors || {})) {
			const seen = merged[path] ?? (merged[path] = []);
			for (const message of messages) {
				if (!seen.includes(message)) seen.push(message);
			}
		}
	}
	return merged;
}

/**
 * Renders merged errors for the error panel, bolding the "<field>: " prefix the
 * error mapping produces. A message without that prefix is shown as it is.
 */
export function toDisplayMessages(mergedErrors) {
	return Object.values(mergedErrors).flat().map((message) => {
		const parts = message.split(':');
		if (parts.length < 2) return { message };

		return { message: <><b>{parts[0]}</b>:{parts.slice(1).join(':')}</> };
	});
}

export function toFieldErrorGroups(apiErrors) {
	if (!Array.isArray(apiErrors)) return [];

	return apiErrors.map((entry) => {
		const errors = {};
		if (!entry || typeof entry !== 'object') return { errors };

		for (const [field, messages] of Object.entries(entry)) {
			// `index` is the payload row; the panel merges rows, so it is dropped.
			if (field === 'index') continue;

			const list = Array.isArray(messages) ? messages : [messages];
			const cleaned = list.filter((m) => typeof m === 'string' && m !== '');
			if (cleaned.length === 0) continue;

			errors[field || PAYLOAD_LABEL] = cleaned;
		}
		return { errors };
	});
}

// Decides what the share page shows when a push is rejected.
//
// The panel can only be used when the dex has the enhanced error UI and the
// response actually carried per-field errors; otherwise the caller falls back to
// a single message, which is what the page did for every failure before.
export function buildPushErrorDisplay(error, { enhanced = false, fallback = '' } = {}) {
	const list = toDisplayMessages(mergeErrorsByField(toFieldErrorGroups(error?.error?.errors)));
	const message = error?.error?.message || error?.message || fallback;

	return enhanced && list.length > 0 ? { list, message } : { list: [], message };
}
